// Hook obslugujacy flow "Wyslij oferte do klienta" - reuzywany przez sticky bar i naglowek oferty.
// Zwraca handlery:
//  - sendOnly: PUT status=6 (oferta -> Do akceptacji)
//  - sendAndOpen:
//      - dla EXTERNAL_ACCEPTANCE: PUT status=6 + redirect na publiczny acceptanceUrl (bez overlogin)
//      - dla pozostalych typow: PUT status=6 + overlogin na MAIN usera klienta + redirect na widok klienta
//  - openInClientPanel:
//      - dla EXTERNAL_ACCEPTANCE: tylko redirect na publiczny acceptanceUrl (bez overlogin)
//      - dla pozostalych typow: overlogin na MAIN usera klienta + redirect na widok klienta

import { useQueryClient } from 'react-query';

import {
  useGetSalesCrmClientUsers,
  usePostOverLogin,
  usePutSalesCrmOffer
} from 'api';
import { useAppNavigate, useNotifications } from 'hooks';
import { reduxActions, useDispatch } from 'store';

interface IUseSendOfferToClientFlowParams {
  clientId: number;
  offerId: string;
  /**
   * Absolutny URL publicznej akceptacji oferty - ustawiony tylko dla typu
   * EXTERNAL_ACCEPTANCE. Gdy podany, flow uzywa publicznego linka zamiast overlogin.
   */
  acceptanceUrl?: string | null;
}

interface IUseSendOfferToClientFlowResult {
  isSending: boolean;
  hasClientUser: boolean;
  /** Czy oferta jest typu publicznej akceptacji (ma acceptanceUrl). */
  isExternalAcceptance: boolean;
  sendOnly: () => Promise<void>;
  sendAndOpen: () => Promise<void>;
  openInClientPanel: () => Promise<void>;
}

export const useSendOfferToClientFlow = ({
  clientId,
  offerId,
  acceptanceUrl
}: IUseSendOfferToClientFlowParams): IUseSendOfferToClientFlowResult => {
  const queryClient = useQueryClient();
  const navigate = useAppNavigate();
  const dispatch = useDispatch();
  const { showErrorMessage } = useNotifications();

  const isExternalAcceptance = !!acceptanceUrl;

  const { data: clientUsers } = useGetSalesCrmClientUsers(
    clientId,
    { page: 1, limit: 50 },
    { enabled: clientId > 0 && !isExternalAcceptance }
  );
  // Priorytet wyboru usera do overlogin: ROLE_USER_MAIN (role_id=1), nastepnie pierwszy z listy.
  const mainClientUser =
    clientUsers?.items?.find((u) => u.role_id === 1) ??
    clientUsers?.items?.find((u) => u.is_main) ??
    clientUsers?.items?.[0] ??
    null;

  const { mutateAsync: putOfferAsync, isLoading: isSavingStatus } = usePutSalesCrmOffer(
    clientId,
    offerId,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['sales-crm', 'client', clientId, 'offer', offerId]);
      }
    }
  );

  const { mutateAsync: postOverLogin } = usePostOverLogin();

  // EXTERNAL_ACCEPTANCE: publiczny link ma format absolutny (http(s)://host/oferta/{token}).
  // Wyciagamy sciezke i nawigujemy SPA-routerem zeby nie wymuszac fullPage reload.
  const goToPublicOffer = (absoluteUrl: string) => {
    try {
      const url = new URL(absoluteUrl);
      const samePath = `${url.pathname}${url.search}${url.hash}`;
      navigate(samePath);
    } catch {
      window.location.href = absoluteUrl;
    }
  };

  const sendOnly = async () => {
    await putOfferAsync({ status: 6 });
  };

  const sendAndOpen = async () => {
    if (isExternalAcceptance && acceptanceUrl) {
      try {
        await putOfferAsync({ status: 6 });
        // Token publiczny generuje listener po transition statusu - URL z GET-a moze byc
        // jeszcze nieaktualny. Inwalidujemy zapytanie i odswiezamy.
        await queryClient.invalidateQueries(['sales-crm', 'client', clientId, 'offer', offerId]);
        goToPublicOffer(acceptanceUrl);
      } catch {
        showErrorMessage('Nie udalo sie wyslac oferty');
      }
      return;
    }

    if (!mainClientUser) {
      showErrorMessage('Brak uzytkownika klienta do przelogowania');
      return;
    }
    try {
      await putOfferAsync({ status: 6 });
      await postOverLogin({ to_switch_user_id: mainClientUser.id });
      dispatch(reduxActions.setCurrentCartId(null));
      dispatch(reduxActions.setOverlogin(mainClientUser.id));
      navigate(`/dashboard/offers/dedicated/${offerId}`);
    } catch {
      showErrorMessage('Nie udalo sie wyslac oferty lub przelogowac');
    }
  };

  // Przejdz do oferty (EXTERNAL_ACCEPTANCE) / Zobacz oferte oczami klienta (pozostale typy):
  // bez zmiany statusu - tylko nawigacja. Uzywane gdy oferta jest juz wyslana
  // (status=6/2) i handlowiec chce podejrzec widok klienta.
  const openInClientPanel = async () => {
    if (isExternalAcceptance && acceptanceUrl) {
      goToPublicOffer(acceptanceUrl);
      return;
    }

    if (!mainClientUser) {
      showErrorMessage('Brak uzytkownika klienta do przelogowania');
      return;
    }
    try {
      await postOverLogin({ to_switch_user_id: mainClientUser.id });
      dispatch(reduxActions.setCurrentCartId(null));
      dispatch(reduxActions.setOverlogin(mainClientUser.id));
      navigate(`/dashboard/offers/dedicated/${offerId}`);
    } catch {
      showErrorMessage('Nie udalo sie przelogowac na klienta');
    }
  };

  return {
    isSending: isSavingStatus,
    hasClientUser: isExternalAcceptance || mainClientUser !== null,
    isExternalAcceptance,
    sendOnly,
    sendAndOpen,
    openInClientPanel
  };
};
